import { cva } from "class-variance-authority";
import {
  AtButton,
  AtButtonSize,
  AtButtonType,
  AtTag,
  AtTagType,
} from "../../atoms";
import { MlNoteProps, NOTE_COLORS } from "./ml-note.types";
import {
  Color,
  ERROR,
  FORMAT_DATE_WITH_HOUR,
  formatDate,
  INFO,
  PRIMARY,
  SUCCESS,
  SURFACE,
  WARNING,
} from "../../../utils";
import { useMemo } from "react";
import { cn } from "../../../utils/cn";
import dayjs from "dayjs";

const getBgColor = () => {
  const max = 6;
  const min = 0;
  const randomIndex = Math.floor(Math.random() * (max - min)) + min;
  return NOTE_COLORS[randomIndex] as Color;
};

export const MlNote = ({
  title,
  body,
  createdAt,
  modifiedAt,
  userName: createdBy,
  "data-testid": testId,
  action,
  color: defaultColor,
  noteVariant = "default",
}: MlNoteProps) => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const color = useMemo(() => defaultColor ?? getBgColor(), []);
  const isCompact = noteVariant === "compact";
  const isEdited = useMemo(
    () => isNoteEdited(createdAt, modifiedAt),
    [createdAt, modifiedAt],
  );

  return (
    <div
      data-testid={testId}
      className={cn(
        variant({ color }),
        isCompact ? "w-full min-h-[14rem]" : "w-[17.5rem] min-h-[21.875rem]",
      )}
    >
      <div className="flex justify-between items-center">
        <p className="font-semibold">
          {title}
          {isEdited && <span className="text-xs font-normal"> (Editada)</span>}
        </p>
        {action && (
          <AtButton
            {...action}
            buttonType={AtButtonType.TERTIARY}
            size={AtButtonSize.EXTRA_SMALL}
            startIcon="edit"
            className="!px-0"
          />
        )}
      </div>
      <p className="flex-1">{body}</p>
      <div
        className={cn(
          "flex flex-wrap gap-2",
          isCompact ? "flex-col items-start" : "flex-row",
        )}
      >
        <AtTag
          type={AtTagType.NOTE}
          text={`Agente ${createdBy}`}
          color={color}
        />
        <AtTag
          type={AtTagType.NOTE}
          text={formatDate(modifiedAt, FORMAT_DATE_WITH_HOUR, true)}
          color={color}
        />
      </div>
    </div>
  );
};

const variant = cva(
  "flex flex-col text-surface-400 text-sm gap-2 p-4 rounded-lg",
  {
    variants: {
      color: {
        [ERROR]: "!bg-error-75",
        [SUCCESS]: "!bg-success-75",
        [INFO]: "!bg-info-75",
        [WARNING]: "!bg-warning-75",
        [PRIMARY]: "!bg-primary-75",
        [SURFACE]: "!bg-surface-75",
      },
    },
    defaultVariants: {
      color: SURFACE,
    },
  },
);

/**
 * Checks if a note has been edited by comparing createdAt and modifiedAt timestamps.
 * Uses a tolerance of 2 seconds to account for backend timing differences.
 * Handles both Unix timestamps (in seconds) and ISO date strings.
 */
const isNoteEdited = (createdAt: string, modifiedAt: string): boolean => {
  // Check if timestamps are Unix timestamps (numeric strings)
  const createdNum = parseFloat(createdAt);
  const modifiedNum = parseFloat(modifiedAt);
  const isUnixTimestamp =
    !isNaN(createdNum) &&
    !isNaN(modifiedNum) &&
    isFinite(createdNum) &&
    isFinite(modifiedNum);

  let created: dayjs.Dayjs;
  let modified: dayjs.Dayjs;

  if (isUnixTimestamp) {
    // Convert Unix timestamp from seconds to milliseconds for dayjs
    created = dayjs(createdNum * 1000);
    modified = dayjs(modifiedNum * 1000);
  } else {
    // Try parsing as regular date strings
    created = dayjs(createdAt);
    modified = dayjs(modifiedAt);
  }

  // If either date is invalid, fall back to string comparison
  if (!created.isValid() || !modified.isValid()) {
    return createdAt !== modifiedAt;
  }

  // Consider the note edited if modifiedAt is more than 2 seconds after createdAt
  // This accounts for any backend timing differences or rounding
  const diffInSeconds = modified.diff(created, "second");
  return diffInSeconds > 2;
};
